import '../../../packages/neb-lit-components/src/components/inputs/neb-textfield';
import '../../../packages/neb-lit-components/src/components/neb-text';

import { html, css } from 'lit';
import moment from 'moment-timezone';

import NebForm, {
  ELEMENTS as ELEMENTS_BASE,
} from '../../../packages/neb-lit-components/src/components/forms/neb-form';
import { BUTTON_ROLE } from '../../../packages/neb-lit-components/src/components/neb-button';
import {
  CSS_FONT_WEIGHT_BOLD,
  CSS_COLOR_HIGHLIGHT,
  CSS_FONT_SIZE_HEADER,
} from '../../../packages/neb-styles/neb-variables';
import * as MASKS from '../../../packages/neb-utils/masks';
import { map } from '../../../packages/neb-utils/utils';
import { isDateOfBirth } from '../../../packages/neb-utils/validators';
import { navigate } from '../../store';

import { SELF_CHECK_IN_METHODS } from './settings/neb-form-self-check-in';

export const ELEMENTS = {
  ...ELEMENTS_BASE,

  dateOfBirthInput: { id: 'input-date-of-birth' },
  firstNameInput: { id: 'input-first-name' },
  lastNameInput: { id: 'input-last-name' },
  lookUpButton: { id: 'button-look-up' },
  cancelButton: { id: 'cancel-button' },
  pinInput: { id: 'input-pin' },
};

const nameAndDOBValidator = selfCheckInMethod => ({
  validate: (v, _keyPath, state) =>
    selfCheckInMethod === SELF_CHECK_IN_METHODS.PIN_ONLY || state.pin || v,
  error: 'Required',
});

class NebFormLookUpAppointment extends NebForm {
  static get properties() {
    return {
      ...super.properties,
      selfCheckInMethod: String,
      enableSelfCheckIn: Boolean,
    };
  }

  static get styles() {
    return [
      super.styles,
      css`
        :host {
          width: auto;
        }

        :host(:not([page])) .header {
          display: flex;
          align-items: center;
          margin: 0;
        }

        .content {
          flex: unset;
          overflow-y: unset;
        }

        .layout {
          grid-template-columns: minmax(315px, 1fr) 1fr;
          gap: 0;
          padding-top: 20px;
        }

        .font-header {
          font-size: ${CSS_FONT_SIZE_HEADER};
          color: ${CSS_COLOR_HIGHLIGHT};
          font-weight: ${CSS_FONT_WEIGHT_BOLD};
        }

        .grid-item {
          grid-column-start: 1;
          grid-column-end: 2;
        }

        .button {
          width: 120px;
        }

        .look-up-button {
          margin-right: 10px;
        }

        .or-separator {
          font-size: 16px;
          text-align: center;
          font-weight: ${CSS_FONT_WEIGHT_BOLD};
          color: ${CSS_COLOR_HIGHLIGHT};
        }
      `,
    ];
  }

  static createModel() {
    return {
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      pin: '',
    };
  }

  createSelectors() {
    return {
      children: {
        firstName: [nameAndDOBValidator(this.selfCheckInMethod)],
        lastName: [nameAndDOBValidator(this.selfCheckInMethod)],
        dateOfBirth: {
          unformat: v => v && moment(v).format('YYYY-MM-DD'),
          validators: [
            nameAndDOBValidator(this.selfCheckInMethod),
            {
              error: isDateOfBirth.error,
              validate: (v, _keyPath, state) =>
                this.selfCheckInMethod === SELF_CHECK_IN_METHODS.PIN_ONLY ||
                state.pin ||
                isDateOfBirth.validate(v),
            },
          ],
        },
        pin: [
          {
            validate: (v, _keyPath, state) =>
              this.selfCheckInMethod ===
                SELF_CHECK_IN_METHODS.NAME_AND_DOB_ONLY ||
              state.firstName ||
              state.lastName ||
              state.dateOfBirth ||
              v,
            error: 'Required',
          },
          {
            validate: v => v.length >= 4 || v.length === 0,
            error: 'Must be at least 4 digits',
          },
        ],
      },
    };
  }

  initState() {
    super.initState();

    this.selfCheckInMethod = SELF_CHECK_IN_METHODS.NAME_AND_DOB_ONLY;
    this.enableSelfCheckIn = true;

    this.onLookUpAppointment = () => {};
  }

  initHandlers() {
    super.initHandlers();
    this.handlers = {
      ...this.handlers,

      lookUp: () => {
        if (this.formService.validate()) {
          const rawModel = this.formService.build();
          const model = map(rawModel, (_, value) =>
            typeof value === 'string' ? value.trim() : value,
          );

          this.onLookUpAppointment(model);
        }
      },
      navigateToRoot: () => {
        navigate('#/');
      },
      changeText: e => {
        this.handlers.change(e);

        if (!e.event) {
          this.formService.validate();
        }
      },
    };
  }

  updated(changedProps) {
    if (changedProps.has('enableSelfCheckIn') && !this.enableSelfCheckIn) {
      navigate('#/');
    }

    super.updated(changedProps);
  }

  renderActionBar() {
    return html`
      <div>
        <neb-button
          id="${ELEMENTS.lookUpButton.id}"
          class="button look-up-button"
          label="Look Up"
          .onClick="${this.handlers.lookUp}"
        ></neb-button>
        <neb-button
          id="${ELEMENTS.cancelButton.id}"
          class="button"
          label="Cancel"
          .role="${BUTTON_ROLE.OUTLINE}"
          .onClick="${this.handlers.navigateToRoot}"
        ></neb-button>
      </div>
    `;
  }

  renderHeader() {
    return html`
      <div class="header">
        <span class="font-header">Appointment Check In</span>
      </div>
    `;
  }

  __renderPINForm() {
    if (this.selfCheckInMethod === SELF_CHECK_IN_METHODS.NAME_AND_DOB_ONLY) {
      return null;
    }

    return html`
      <neb-textfield
        id="${ELEMENTS.pinInput.id}"
        class="grid-item"
        label="PIN"
        name="pin"
        helper=" "
        .error="${this.errors.pin}"
        .value="${this.state.pin}"
        .onChange="${this.handlers.changeText}"
        inputMode="numeric"
        .mask="${MASKS.number}"
        .maxLength="${20}"
        type="password"
      ></neb-textfield>
    `;
  }

  __renderNameDOBForm() {
    if (this.selfCheckInMethod === SELF_CHECK_IN_METHODS.PIN_ONLY) {
      return null;
    }

    return html`
      <neb-textfield
        id="${ELEMENTS.firstNameInput.id}"
        class="grid-item"
        label="First Name"
        name="firstName"
        helper=" "
        .error="${this.errors.firstName}"
        .value="${this.state.firstName}"
        .onChange="${this.handlers.changeText}"
      ></neb-textfield>

      <neb-textfield
        id="${ELEMENTS.lastNameInput.id}"
        class="grid-item"
        label="Last Name"
        name="lastName"
        helper=" "
        .error="${this.errors.lastName}"
        .value="${this.state.lastName}"
        .onChange="${this.handlers.changeText}"
      ></neb-textfield>

      <neb-textfield
        id="${ELEMENTS.dateOfBirthInput.id}"
        class="grid-item"
        label="Date of Birth (MM/DD/YYYY)"
        placeholder="Date of Birth (MM/DD/YYYY)"
        name="dateOfBirth"
        helper=" "
        .mask="${MASKS.date}"
        .error="${this.errors.dateOfBirth}"
        .value="${this.state.dateOfBirth}"
        .onChange="${this.handlers.changeText}"
        .inputMode="${'numeric'}"
      ></neb-textfield>
    `;
  }

  __renderOr() {
    if (this.selfCheckInMethod !== SELF_CHECK_IN_METHODS.BOTH) {
      return null;
    }

    return html` <div class="grid-item or-separator">- OR -</div> `;
  }

  renderContent() {
    if (!this.__selfCheckInMethod) return '';

    return html`
      ${this.__renderPINForm()} ${this.__renderOr()}
      ${this.__renderNameDOBForm()}
    `;
  }
}

customElements.define('neb-form-look-up-appointment', NebFormLookUpAppointment);
